<template>
  <div class="container">
    <HMModalHeader
      :title="$t('PODCASTS.TITLES.MEMBERS')"
      @close="$emit('close')"
    />
    <div ref="wrapper" class="w-100">
      <div
        class="d-flex flex-grow-1 text-truncate justify-content-between align-items-center mb-3"
      >
        <div class="text-wrap media-title">
          <h4 class="pr-2">{{ podcast.title }}</h4>
        </div>
        <span class="community-members-count">{{
          $tc('PODCASTS.TEXT.LISTENERS_VARIANT', podcast.num_members, {
            count: podcast.num_members,
          })
        }}</span>
      </div>
      <HMSearch v-model="search" class="mb-3" />
      <HMPaginatedTable
        ref="podcast-members-table"
        api="podcasts/loadMembers"
        :fields="tableFields"
        :query="query"
        name="podcast-members-table"
        class="podcast-members-table"
        :showPaginationButtons="false"
      >
        <template slot="name" slot-scope="{ data }">
          <div
            @click="viewProfile(data)"
            class="w-100 cursor-pointer d-flex flex-row"
          >
            <img
              v-if="data.community_profile_photo"
              :src="data.community_profile_photo"
              class="member-icon mr-2"
              alt="avatar"
            />
            <img
              v-else
              src="/assets/images/avatar.svg"
              class="member-icon mr-2"
              alt="avatar"
            />
            <span class="text-dark" :class="{ 'crossed-name': data.is_banned }">
              {{ data.name }}
            </span>
          </div>
        </template>
        <template slot="status" slot-scope="{ data }">
          <div class="d-flex align-items-center">
            <font-awesome-icon
              :icon="['fas', 'circle']"
              class="dot mr-2 status"
              :class="getCircleClass(data)"
            />
            <span class="text-dark">{{ getStatus(data) }}</span>
          </div>
        </template>
        <template v-if="canBanUser(data)" slot="actions" slot-scope="{ data }">
          <HMIconButton
            :icon="['far', 'ellipsis-v']"
            :id="`popover-${data.id}`"
          />
          <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
            <HMPopoverMenuItem
              v-if="!data.is_banned"
              v-confirm="{
                title: $t('PODCASTS.LINK.BAN_MEMBER'),
                text: $t('PODCASTS.TEXT.BAN_MEMBER_DESCRIPTION'),
                confirmButton: $t('PODCASTS.LINK.BAN_MEMBER'),
                confirm: () => banMember(podcast.id, data),
              }"
            >
              {{ $t('PODCASTS.LINK.BAN_LISTENER') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem v-else @click="unBanMember(podcast.id, data)">
              {{ $t('PODCASTS.LINK.UNBAN_LISTENER') }}
            </HMPopoverMenuItem>
          </HMPopover>
        </template>
      </HMPaginatedTable>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { errorToMessage } from '@/core/utils/errors';
import { mapGetters } from 'vuex';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';

export default Vue.extend({
  name: 'PodcastMembersModal',
  computed: {
    ...mapGetters({
      teacherStudioUrl: 'user/teacherStudioUrl',
      teacherMagicCode: 'user/teacherMagicCode',
      isStudioOwner: 'user/isStudioOwner',
      currentUser: 'user/user',
    }),
    query() {
      return {
        q: this.search,
        podcastId: this.podcast.id,
      };
    },
  },
  props: {
    podcast: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      search: '',
      members: [],
      tableFields: [
        {
          name: 'name',
          title: this.$t('COMMUNITY.TABLE.NAME'),
        },
        {
          name: 'status',
          title: this.$t('COMMUNITY.TABLE.STATUS'),
        },
        {
          name: 'actions',
          title: '',
        },
      ],
      numMembers: null,
    };
  },
  methods: {
    async onDiscard() {
      if (this.discard) {
        await this.discard();
      }
      await this.$emit('close');
    },
    async banMember(podcastId, member) {
      try {
        await this.$store.dispatch('podcasts/banMember', {
          podcastId,
          memberId: member.id,
        });
        member.is_banned = true;
        this.podcast.num_members -= 1;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PODCASTS.NOTIFICATIONS.MEMBER_WAS_SUCCESSFULLY_BANNED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async unBanMember(podcastId, member) {
      try {
        await this.$store.dispatch('podcasts/unBanMember', {
          podcastId,
          memberId: member.id,
        });
        member.is_banned = false;
        this.podcast.num_members += 1;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PODCASTS.NOTIFICATIONS.MEMBER_WAS_SUCCESSFULLY_UNBANNED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    viewProfile(data) {
      const route = this.$router.resolve({
        name: 'student-community-profile-view',
        params: {
          id: data.id,
          studio_url: this.teacherStudioUrl,
        },
      });
      const routeUrl = route.href.replaceAll(`${this.teacherStudioUrl}/`, '');
      const url = getStudentViewURL(
        this.teacherStudioUrl,
        routeUrl,
        this.teacherMagicCode
      );
      window.open(url, '_blank');
    },
    getStatus(value) {
      return value.is_banned
        ? this.$t('COMMUNITY.TEXT.BANNED')
        : value.is_admin
        ? this.$t('COMMUNITY.TEXT.ADMIN')
        : this.$t('COMMUNITY.TEXT.MEMBER');
    },
    getCircleClass(value) {
      return value.is_banned
        ? 'status--banned'
        : value.is_admin
        ? 'status--admin'
        : 'status--member';
    },
    canBanUser(data) {
      return (
        !data.is_admin ||
        (this.isStudioOwner && data.id != this.currentUser.community_profile_id)
      );
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.v-select {
  .vs__dropdown-menu {
    z-index: 2;
    position: relative;
  }
}

.community-members-count {
  color: $dark-gray;
}

.community-members-table {
  overflow-y: auto;
  //max-height: 300px;
}

.banned-member {
  color: $coral;
}

.dot {
  margin-right: 0.25rem;
  font-size: 0.5rem;
}

.status {
  &--admin {
    color: $light-green;
  }

  &--member {
    color: $dark-gray;
  }

  &--banned {
    color: $coral;
  }
}

.crossed-name {
  text-decoration: line-through;
}

.cursor-pointer {
  cursor: pointer;
}

.member-icon {
  height: 24px;
  width: 24px;
  border-radius: 50%;
}
</style>
